import { AppShape } from "@eave/shared/types/api";
import { WorksheetBaseType } from "@eave/shared/types/api/worksheets";
import { formatCurrency } from "@eave/shared/utils/currency";
import { IWorksheetState } from "underwriter_dashboard/shared/WorksheetEditor/interfaces";
import { WS_NAME } from "underwriter_dashboard/shared/WorksheetEditor/wsDefs";
import { getWsById, getWsByName } from "underwriter_dashboard/shared/WorksheetEditor/wsHelpers";
import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";

type SellerCreditsComputedValues = {
  readonly purchase_price: string;
  readonly percentage_amount: string;
  readonly final_amount: string;
};

export type SellerCredit = WorksheetBaseType &
  SellerCreditsComputedValues & {
    method: "FixedAmount" | "PercentageOfPurchasePrice";
    fixed_amount: string;
    percentage_of_purchase_price: string;
    fixed_or_percentage_amount: string;
    manual_override_amount: string;
  };

export const sellerCreditsHeaderData = worksheets => {
  // A null value means the `method` is not yet initialized
  if (worksheets.method === null) {
    // These headers were based off the behavior of `WorksheetEditor`
    return makeHeaderData({
      method: "Description",
      manual_override_amount: "Manual Override Amount",
      final_amount: "Final Amount",
    });
  }
  if (worksheets.method === "FixedAmount") {
    return makeHeaderData({
      method: "Description",
      fixed_amount: "Fixed Amount",
      manual_override_amount: "Manual Override Amount",
      final_amount: "Final Amount",
    });
  }

  if (worksheets.method === "PercentageOfPurchasePrice") {
    return makeHeaderData({
      method: "Description",
      purchase_price: "Purchase Price",
      percentage_of_purchase_price: "Percentage of Purchase Price (%)",
      percentage_amount: "Percentage Amount ($)",
      manual_override_amount: "Manual Override Amount",
      final_amount: "Final Amount",
    });
  }
};

export const sellerCreditToRow = (p: SellerCredit) => {
  if (!p) return {};
  return {
    ...p,
    final_amount: p.final_amount ? formatCurrency(p.final_amount, 2, 2) : "-",
    percentage_amount: p.percentage_amount ? formatCurrency(p.percentage_amount, 2, 2) : "-",
    manual_override_amount: p.manual_override_amount
      ? formatCurrency(p.manual_override_amount, 2, 2)
      : "-",
    purchase_price: p.purchase_price ? formatCurrency(p.purchase_price, 2, 2) : "-",
  };
};

/** Returns the seller credits worksheet with nested slices' instances mapped to the top-level
 * @param  {AppShape} app
 * @param  {IWorksheetState[]} worksheets
 * @param  {React.Dispatch<any>} setWorksheets
 */
export const loadSellerCredits = (
  app: AppShape,
  worksheets: IWorksheetState[],
  setWorksheets: React.Dispatch<any>,
) => {
  const sellerCreditsWs = getWsByName(worksheets, WS_NAME.SELLER_CREDITS);

  if (!sellerCreditsWs || !sellerCreditsWs.instance) {
    throw new Error(`could not load seller credits worksheet on app ${app.id}`);
  }

  const {
    id,
    instance: { version: instanceVersion },
  } = sellerCreditsWs;

  const processedWorksheet: SellerCredit | any = {
    id: sellerCreditsWs.id,
    version: instanceVersion,
    ...flattenWorksheetState(getWsById(worksheets, id)),
  };

  setWorksheets(processedWorksheet);
};

/** Flattens an IWorksheetState for terser consumption
 * @param  {IWorksheetState} worksheet
 * @param  {number} array_index?
 */
const flattenWorksheetState = (worksheet: IWorksheetState) => {
  const { instance } = worksheet;

  // The "id" property exists at both levels (IWorksheetState and IWorksheetState.instance), so rename the instance's id accordingly
  const instanceModified = { ...instance, instanceId: instance.id };

  // Clean up some keys
  delete instanceModified.id;

  return {
    ...worksheet,
    ...instanceModified,
  };
};
